/* eslint-disable max-lines */

import type { AfterViewInit, OnInit } from '@angular/core';
import { ChangeDetectorRef, Component, ElementRef, ViewChild } from '@angular/core';

import { IconButtonComponent } from '../common/button/components/icon-button/icon-button.component';
import { ColorPickerService } from './color-picker.service';
import { Hsla, Hsva, Rgba } from './formats';
import { SliderDimension, SliderDirective, SliderPosition, TextDirective } from './helpers';

@Component({
  selector: 'rt-color-picker',
  templateUrl: './color-picker.component.html',
  styleUrls: ['./color-picker.component.less'],
  imports: [SliderDirective, TextDirective, IconButtonComponent],
})
export class ColorPickerComponent implements OnInit, AfterViewInit {
  @ViewChild('hueSlider', { static: true }) public hueSlider: ElementRef;
  @ViewChild('alphaSlider', { static: true }) public alphaSlider: ElementRef;
  @ViewChild('dialogPopup', { static: true }) public dialogElement: ElementRef;

  public cpPosition: string;
  public cpPositionOffset: number;
  public cpOutputFormat: string;
  public cpPresetLabel: string;
  public cpPresetColors: string[];
  public cpCancelButton: boolean;
  public cpCancelButtonClass: string;
  public cpCancelButtonText: string;
  public cpOKButton: boolean;
  public cpOKButtonClass: string;
  public cpOKButtonText: string;
  public cpHeight: number;
  public cpWidth: number;
  public cpIgnoredElements: any;
  public cpDialogDisplay: string;
  public cpSaveClickOutside: boolean;
  public cpAlphaChannel: string;

  public rgbaText: Rgba;
  public hslaText: Hsla;
  public selectedColor: string;
  public alphaSliderColor: string;
  public hueSliderColor: string;
  public slider: SliderPosition;
  public show: boolean;
  public hidden: boolean;
  public top: number;
  public left: number;
  public position: string;
  public format: number;
  public hexText: string;

  private hsva: Hsva;
  private outputColor: string;
  private sliderDimMax: SliderDimension;
  private directiveInstance: any;
  private initialColor: string;
  private directiveElementRef: ElementRef;

  private listenerMouseDown: any;
  private listenerResize: any;

  constructor(
    private el: ElementRef,
    private cdRef: ChangeDetectorRef,
    private service: ColorPickerService,
  ) {}

  public ngOnInit(): void {
    const alphaWidth = this.alphaSlider.nativeElement.offsetWidth;
    const hueWidth = this.hueSlider.nativeElement.offsetWidth;
    this.sliderDimMax = new SliderDimension(hueWidth, this.cpWidth, 130, alphaWidth);
    this.slider = new SliderPosition(0, 0, 0, 0);
    if (this.cpOutputFormat === 'rgba') {
      this.format = 1;
    } else if (this.cpOutputFormat === 'hsla') {
      this.format = 2;
    } else {
      this.format = 0;
    }
    this.listenerMouseDown = (event: any) => {
      this.onMouseDown(event);
    };
    this.listenerResize = () => {
      this.onResize();
    };
    this.openDialog(this.initialColor, false);
  }

  public ngAfterViewInit(): void {
    if (this.cpWidth !== 230) {
      const alphaWidth = this.alphaSlider.nativeElement.offsetWidth;
      const hueWidth = this.hueSlider.nativeElement.offsetWidth;
      this.sliderDimMax = new SliderDimension(hueWidth, this.cpWidth, 130, alphaWidth);
      this.update(false);
      this.cdRef.detectChanges();
    }
  }

  // eslint-disable-next-line max-lines-per-function, max-params-no-constructor/max-params-no-constructor
  public setDialog(
    instance: any,
    elementRef: ElementRef,
    color: any,
    cpPosition: string,
    cpPositionOffset: string,
    cpOutputFormat: string,
    cpPresetLabel: string,
    cpPresetColors: string[],
    cpCancelButton: boolean,
    cpCancelButtonClass: string,
    cpCancelButtonText: string,
    cpOKButton: boolean,
    cpOKButtonClass: string,
    cpOKButtonText: string,
    cpHeight: string,
    cpWidth: string,
    cpIgnoredElements: any,
    cpDialogDisplay: string,
    cpSaveClickOutside: boolean,
    cpAlphaChannel: string,
  ): void {
    this.directiveInstance = instance;
    this.initialColor = color;
    this.directiveElementRef = elementRef;
    this.cpPosition = cpPosition;
    this.cpPositionOffset = parseInt(cpPositionOffset, 10);
    this.cpOutputFormat = cpOutputFormat;
    this.cpPresetLabel = cpPresetLabel;
    this.cpPresetColors = cpPresetColors;
    this.cpCancelButton = cpCancelButton;
    this.cpCancelButtonClass = cpCancelButtonClass;
    this.cpCancelButtonText = cpCancelButtonText;
    this.cpOKButton = cpOKButton;
    this.cpOKButtonClass = cpOKButtonClass;
    this.cpOKButtonText = cpOKButtonText;
    this.cpHeight = parseInt(cpHeight, 10);
    this.cpWidth = parseInt(cpWidth, 10);
    if (!this.cpWidth) {
      this.cpWidth = elementRef.nativeElement.offsetWidth;
    }
    this.cpIgnoredElements = cpIgnoredElements;
    this.cpDialogDisplay = cpDialogDisplay;
    this.cpSaveClickOutside = cpSaveClickOutside;
    this.cpAlphaChannel = cpAlphaChannel;
  }

  // Used by color-picker.directive
  public setInitialColor(color: any): void {
    this.initialColor = color;
  }

  // Used by color-picker.directive
  public setPresetConfig(cpPresetLabel: string, cpPresetColors: string[]): void {
    this.cpPresetLabel = cpPresetLabel;
    this.cpPresetColors = cpPresetColors;
  }

  public cancelColor(): void {
    this.setColorFromString(this.initialColor, true);
    if (this.cpDialogDisplay === 'popup') {
      this.directiveInstance.colorChanged(this.initialColor, true);
      this.closeColorPicker();
    }
  }

  public oKColor(): void {
    if (this.cpDialogDisplay === 'popup') {
      this.closeColorPicker();
    }

    if (this.outputColor) {
      this.directiveInstance.colorSelected(this.outputColor);
    }
  }

  public setColorFromString(value: string, emit: boolean = true): void {
    let hsva: Hsva;
    if (this.cpAlphaChannel === 'hex8') {
      hsva = this.service.stringToHsva(value, true);
      if (!hsva && !this.hsva) {
        hsva = this.service.stringToHsva(value, false);
      }
    } else {
      hsva = this.service.stringToHsva(value, false);
    }
    if (hsva) {
      this.hsva = hsva;
      this.update(emit);
    }
  }

  public setSaturation(val: { v: number; rg: number }): void {
    const hsla = this.service.hsva2hsla(this.hsva);
    hsla.s = val.v / val.rg;
    this.hsva = this.service.hsla2hsva(hsla);
    this.update();

    this.directiveInstance.inputChanged({ slider: 'saturation', value: val });
  }

  public setLightness(val: { v: number; rg: number }): void {
    const hsla = this.service.hsva2hsla(this.hsva);
    hsla.l = val.v / val.rg;
    this.hsva = this.service.hsla2hsva(hsla);
    this.update();

    this.directiveInstance.inputChanged({ slider: 'lightness', value: val });
  }

  public setHue(val: { v: number; rg: number }): void {
    this.hsva.h = val.v / val.rg;
    this.update();

    this.directiveInstance.sliderChanged({ slider: 'hue', value: val });
  }

  public setAlpha(val: { v: number; rg: number }): void {
    this.hsva.a = val.v / val.rg;
    this.update();

    this.directiveInstance.sliderChanged({ slider: 'alpha', value: val });
  }

  public setR(val: { v: number; rg: number }): void {
    const rgba = this.service.hsvaToRgba(this.hsva);
    rgba.r = val.v / val.rg;
    this.hsva = this.service.rgbaToHsva(rgba);
    this.update();

    this.directiveInstance.inputChanged({ slider: 'red', value: val });
  }

  public setG(val: { v: number; rg: number }): void {
    const rgba = this.service.hsvaToRgba(this.hsva);
    rgba.g = val.v / val.rg;
    this.hsva = this.service.rgbaToHsva(rgba);
    this.update();

    this.directiveInstance.inputChanged({ slider: 'green', value: val });
  }

  public setB(val: { v: number; rg: number }): void {
    const rgba = this.service.hsvaToRgba(this.hsva);
    rgba.b = val.v / val.rg;
    this.hsva = this.service.rgbaToHsva(rgba);
    this.update();

    this.directiveInstance.inputChanged({ slider: 'blue', value: val });
  }

  public setA(val: { v: number; rg: number }): void {
    this.hsva.a = val.v / val.rg;
    this.update();

    this.directiveInstance.inputChanged({ slider: 'alpha', value: val });
  }

  public setHex(val: string): void {
    this.setColorFromString(val);

    this.directiveInstance.inputChanged({ slider: 'hex', value: val });
  }

  public setSaturationAndBrightness(val: { s: number; v: number; rgX: number; rgY: number }): void {
    this.hsva.s = val.s / val.rgX;
    this.hsva.v = val.v / val.rgY;
    this.update();
    this.directiveInstance.sliderChanged({ slider: 'saturation-lightness', value: val });
  }

  public formatPolicy(): number {
    this.format = (this.format + 1) % 3;
    if (this.format === 0 && this.hsva.a < 1 && this.cpAlphaChannel === 'hex6') {
      this.format++;
    }
    this.cdRef.detectChanges();
    return this.format;
  }

  public trackBy(index: number, item: string): string {
    return item;
  }

  public openDialog(color: any, emit: boolean = true): void {
    this.setInitialColor(color);
    this.setColorFromString(color, emit);
    this.openColorPicker();
  }

  public closeColorPicker(): void {
    if (this.show) {
      this.show = false;
      this.directiveInstance.toggle(false);
      document.removeEventListener('mousedown', this.listenerMouseDown);
      window.removeEventListener('resize', this.listenerResize);
      this.cdRef.detectChanges();
    }
  }

  private onMouseDown(event: any): void {
    if (
      !this.isDescendant(this.el.nativeElement, event.target) &&
      event.target !== this.directiveElementRef.nativeElement &&
      this.cpIgnoredElements.filter((item: any) => item === event.target).length === 0 &&
      this.cpDialogDisplay === 'popup'
    ) {
      if (!this.cpSaveClickOutside) {
        this.setColorFromString(this.initialColor, false);
        this.directiveInstance.colorChanged(this.initialColor);
      }
      this.closeColorPicker();
    }
  }

  private openColorPicker(): void {
    if (!this.show) {
      this.show = true;
      this.hidden = true;
      setTimeout(() => {
        this.setDialogPosition();
        this.hidden = false;
        this.cdRef.detectChanges();
      }, 0);
      this.directiveInstance.toggle(true);
      document.addEventListener('mousedown', this.listenerMouseDown);
      window.addEventListener('resize', this.listenerResize);
    }
  }

  private onResize(): void {
    // TODO: Fix this the next time the file is edited.
    if (this.position === 'fixed') {
      this.setDialogPosition();
    }
  }

  // eslint-disable-next-line max-lines-per-function
  private setDialogPosition(): void {
    const dialogHeight = this.dialogElement.nativeElement.offsetHeight;
    let boxDirective;
    let node = this.directiveElementRef.nativeElement,
      position = 'static';
    let parentNode: any = null;
    while (node !== null && node.tagName !== 'HTML') {
      position = window.getComputedStyle(node).getPropertyValue('position');
      if (position !== 'static' && parentNode === null) {
        parentNode = node;
      }
      if (position === 'fixed') {
        break;
      }
      node = node.parentNode;
    }
    if (position !== 'fixed') {
      boxDirective = this.createBox(this.directiveElementRef.nativeElement, true);
      if (parentNode === null) {
        parentNode = node;
      }
      const boxParent = this.createBox(parentNode, true);
      this.top = boxDirective.top - boxParent.top;
      this.left = boxDirective.left - boxParent.left + 4;
    } else {
      boxDirective = this.createBox(this.directiveElementRef.nativeElement, false);
      this.top = boxDirective.top;
      this.left = boxDirective.left;
      this.position = 'fixed';
    }
    if (this.cpPosition === 'left') {
      this.top += (boxDirective.height * this.cpPositionOffset) / 100;
      this.left -= this.cpWidth - 4;
    } else if (this.cpPosition === 'top') {
      this.top -= dialogHeight - 4;
      this.left += (this.cpPositionOffset / 100) * boxDirective.width;
    } else if (this.cpPosition === 'bottom') {
      this.top += boxDirective.height + 4;
      this.left += (this.cpPositionOffset / 100) * boxDirective.width;
    } else {
      this.top += boxDirective.height * this.cpPositionOffset;
      this.left += boxDirective.width;
    }
  }

  // eslint-disable-next-line max-lines-per-function
  private update(emit: boolean = true): void {
    if (this.sliderDimMax) {
      const hsla = this.service.hsva2hsla(this.hsva);
      const rgba = this.service.denormalizeRGBA(this.service.hsvaToRgba(this.hsva));
      const hueRgba = this.service.denormalizeRGBA(
        this.service.hsvaToRgba(new Hsva(this.hsva.h, 1, 1, 1)),
      );

      this.hslaText = new Hsla(
        Math.round(hsla.h * 360),
        Math.round(hsla.s * 100),
        Math.round(hsla.l * 100),
        Math.round(hsla.a * 100) / 100,
      );
      this.rgbaText = new Rgba(rgba.r, rgba.g, rgba.b, Math.round(rgba.a * 100) / 100);
      this.hexText = this.service.hexText(rgba, this.cpAlphaChannel === 'hex8');

      this.alphaSliderColor = 'rgb(' + rgba.r + ',' + rgba.g + ',' + rgba.b + ')';
      this.hueSliderColor = 'rgb(' + hueRgba.r + ',' + hueRgba.g + ',' + hueRgba.b + ')';

      if (this.format === 0 && this.hsva.a < 1 && this.cpAlphaChannel === 'hex6') {
        this.format++;
      }

      const lastOutput = this.outputColor;
      this.outputColor = this.service.outputFormat(
        this.hsva,
        this.cpOutputFormat,
        this.cpAlphaChannel === 'hex8',
      );
      this.selectedColor = this.service.outputFormat(this.hsva, 'rgba', false);

      this.slider = new SliderPosition(
        this.hsva.h * this.sliderDimMax.h - 8,
        this.hsva.s * this.sliderDimMax.s - 8,
        (1 - this.hsva.v) * this.sliderDimMax.v - 8,
        this.hsva.a * this.sliderDimMax.a - 8,
      );

      if (emit && lastOutput !== this.outputColor) {
        this.directiveInstance.colorChanged(this.outputColor);
      }
    }
  }

  private isDescendant(parent: any, child: any): boolean {
    let node: any = child.parentNode;
    while (node !== null) {
      if (node === parent) {
        return true;
      }
      node = node.parentNode;
    }
    return false;
  }

  private createBox(element: any, offset: boolean): any {
    return {
      top: element.getBoundingClientRect().top + (offset ? window.pageYOffset : 0),
      left: element.getBoundingClientRect().left + (offset ? window.pageXOffset : 0),
      width: element.offsetWidth,
      height: element.offsetHeight,
    };
  }
}
